import { useEffect, useState, useRef, useCallback } from 'react';
import { FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getTutorials, settutorialUser } from '../../service/serviceChat/tutorialService';
import { useAuth } from '../../contexts/auth';
import * as S from './styled';

import { Pdf, Video, Image } from './types';

const renderMediaComponent = ({
    type,
    url,
    onVideoThreshold,
    initialTime,
    onTimeUpdate,
    onSaveProgress
}) => {

    if (!url) {
        return null;
    }

    switch (type) {
        case 'pdf':
            return <Pdf url={url} />;
        case 'video':
            return (
                <Video
                    key={url}
                    url={url}
                    initialTime={initialTime}
                    onThresholdReached={onVideoThreshold}
                    onTimeUpdate={onTimeUpdate}
                    onSaveProgress={onSaveProgress}
                />
            );
        case 'image':
        case 'img':
            return <Image url={url} />;
        default:
            return null;
    }
};

export function Tutorial() {
    const { setTutorialInactive } = useAuth();
    const [tutorials, setTutorials] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isNextLoading, setIsNextLoading] = useState(false);
    const [isPrevLoading, setIsPrevLoading] = useState(false);
    const [canProceed, setCanProceed] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const mediaContainerRef = useRef(null);
    const currentVideoTimeRef = useRef(0);
    const tutorialsRef = useRef(tutorials);
    tutorialsRef.current = tutorials;

    const handleVideoTimeUpdate = useCallback((curSec) => {
        currentVideoTimeRef.current = curSec;
    }, []);

    const handleSaveVideoProgress = useCallback(async (seconds) => {
        const currentTutorial = tutorialsRef.current[currentStep];
        if (!currentTutorial || currentTutorial.tipo_arquivo !== 'video') return;
        currentVideoTimeRef.current = seconds;
        try {
            await settutorialUser(currentTutorial.id, 'progressing', seconds);
        } catch (err) {
            console.error('Erro ao salvar progresso do tutorial:', err);
        }
    }, [currentStep]);

    useEffect(() => {
        if (tutorials.length > 0 && tutorials[currentStep]) {
            currentVideoTimeRef.current = Number(tutorials[currentStep].tempo) || 0;
        }
    }, [currentStep, tutorials]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const handleVideoThreshold = useCallback(() => {
        setCanProceed(true);
    }, []);

    useEffect(() => {
        if (tutorials.length === 0) return;
        const currentTutorial = tutorials[currentStep];

        if (currentTutorial && currentTutorial.status === 'finished') {
            setCanProceed(true);
            setCountdown(0);
            return;
        }

        setCanProceed(false);

        if (currentTutorial && currentTutorial.tipo_arquivo === 'video') {
            setCountdown(0);
            return;
        }

        setCountdown(5);

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setCanProceed(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [currentStep, tutorials.length]);

    useEffect(() => {
        async function fetchTutorials() {
            try {
                const response = await getTutorials(true);
                if (response && response.tutorials) {
                    const sortedTutorials = response.tutorials
                        .sort((a, b) => a.ordem - b.ordem);

                    const firstPendingIndex = sortedTutorials.findIndex(
                        (t) => t.status !== 'finished'
                    );

                    if (firstPendingIndex === -1) {
                        setTutorialInactive();
                        window.location.href = '/window';
                        return;
                    }

                    const firstTutorial = sortedTutorials[firstPendingIndex];
                    const initialTempo = firstTutorial?.tipo_arquivo === 'video'
                        ? (Number(firstTutorial.tempo) || 0)
                        : undefined;

                    try {
                        await settutorialUser(firstTutorial.id, 'progressing', initialTempo);
                    } catch (statusError) {
                        console.error('Erro ao definir status inicial como progressing:', statusError);
                    }

                    setTutorials(sortedTutorials);
                    setCurrentStep(firstPendingIndex);
                    currentVideoTimeRef.current = initialTempo || 0;
                }
            } catch (error) {
                console.error('Erro na requisição API:', error);
                toast.error('Erro ao buscar os tutoriais.',
                    { position: "top-center", theme: "colored" }
                );
            } finally {
                setLoading(false);
            }
        }
        fetchTutorials();
    }, []);

    const toggleFullscreen = () => {
        const element = mediaContainerRef.current;
        if (!element) return;

        if (!isFullscreen) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    const handleNext = async () => {
        if (isNextLoading || isPrevLoading) return;

        const currentTutorial = tutorials[currentStep];
        setIsNextLoading(true);
        try {
            const isCurrentVideo = currentTutorial.tipo_arquivo === 'video';
            const currentTempo = isCurrentVideo
                ? Math.floor(currentVideoTimeRef.current ?? Number(currentTutorial.tempo) ?? 0)
                : undefined;

            await settutorialUser(currentTutorial.id, 'finished', currentTempo);

            if (currentStep < tutorials.length - 1) {
                const nextTutorial = tutorials[currentStep + 1];
                const isNextVideo = nextTutorial.tipo_arquivo === 'video';
                const nextTempo = isNextVideo ? (Number(nextTutorial.tempo) || 0) : undefined;
                await settutorialUser(nextTutorial.id, 'progressing', nextTempo);
            }
            setTutorials(prev => {
                const newTutorials = [...prev];
                newTutorials[currentStep] = {
                    ...newTutorials[currentStep],
                    status: 'finished',
                    ...(isCurrentVideo ? { tempo: currentTempo } : {})
                };
                return newTutorials;
            });
        } catch (error) {
            console.error('Erro ao salvar o progresso do tutorial', error);
        } finally {
            setIsNextLoading(false);
        }

        if (currentStep < tutorials.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            if (isFullscreen) {
                try {
                    if (document.exitFullscreen) await document.exitFullscreen();
                } catch (e) {
                    console.error(e);
                }
            }
            setTutorialInactive();
            window.location.href = '/window';
        }
    };

    const handlePrevious = async () => {
        if (isNextLoading || isPrevLoading) return;

        if (currentStep > 0) {
            // const prevTutorial = tutorials[currentStep - 1];
            // setIsPrevLoading(true);
            // try {
            //     await settutorialUser(prevTutorial.id, 'progressing');
            // } catch (error) {
            //     console.error('Erro ao salvar o progressing do tutorial', error);
            // } finally {
            //     setIsPrevLoading(false);
            // }
            setCurrentStep(currentStep - 1);
        }
    };

    if (loading) {
        return (
            <S.Container>
                <S.LoadingContainer>
                    <FaSpinner size={44} color="#1D86FF" />
                    <span>Carregando conteúdo...</span>
                </S.LoadingContainer>
            </S.Container>
        );
    }

    if (tutorials.length === 0) {
        return (
            <S.Container>
                <S.TutorialCard style={{ alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ fontSize: '1.2rem', color: '#475569', marginBottom: '1.5rem' }}>Nenhum tutorial encontrado.</p>
                    <S.Actions style={{ width: '100%', justifyContent: 'center' }}>
                        <button className='btn-next' onClick={() => window.location.href = '/window'}>
                            Começar
                        </button>
                    </S.Actions>
                </S.TutorialCard>
            </S.Container>
        );
    }

    const currentTutorial = tutorials[currentStep];
    const isLastStep = currentStep === tutorials.length - 1;
    const progressPercent = ((currentStep + 1) / tutorials.length) * 100;
    const isButtonsDisabled = isNextLoading || isPrevLoading;

    return (
        <S.Container>
            <S.TutorialCard>
                <S.ProgressBar>
                    <S.ProgressFill progress={progressPercent} />
                </S.ProgressBar>

                {/* <S.HeaderRow>
                    <S.StepBadge>Etapa {currentStep + 1} de {tutorials.length}</S.StepBadge>
                    <S.ImmersiveButton onClick={toggleFullscreen} title="Assistir em Tela Cheia" disabled={isButtonsDisabled}>
                        <FaExpand size={12} />
                        Tela Cheia
                    </S.ImmersiveButton>
                </S.HeaderRow> */}

                <h2>{currentTutorial.title}</h2>
                <p className="description">{currentTutorial.description}</p>

                <S.MediaContainer ref={mediaContainerRef}>
                    {renderMediaComponent({
                        type: currentTutorial.tipo_arquivo,
                        url: currentTutorial.url,
                        onVideoThreshold: handleVideoThreshold,
                        initialTime: Number(currentTutorial.tempo) || 0,
                        onTimeUpdate: handleVideoTimeUpdate,
                        onSaveProgress: handleSaveVideoProgress
                    })}
                </S.MediaContainer>

                <S.Actions>
                    {currentStep > 0 ? (
                        <button
                            className='btn-prev'
                            onClick={handlePrevious}
                            disabled={isButtonsDisabled}
                        >
                            {isPrevLoading ? (
                                <S.ButtonSpinner size={12} />
                            ) : (
                                <FaChevronLeft size={12} />
                            )}
                            Voltar
                        </button>
                    ) : (
                        <div style={{ width: '80px' }} />
                    )}

                    <S.StepIndicator style={{ flexDirection: 'column', gap: '4px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {tutorials.map((_, index) => (
                                <S.StepDot key={index} active={index === currentStep} />
                            ))}
                        </div>
                        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>
                            {currentStep + 1} de {tutorials.length}
                        </span>
                    </S.StepIndicator>

                    <button
                        className='btn-next'
                        onClick={handleNext}
                        disabled={isButtonsDisabled || !canProceed}
                    >
                        {isNextLoading ? (
                            <>
                                Carregando
                                <S.ButtonSpinner size={12} style={{ marginLeft: '8px' }} />
                            </>
                        ) : !canProceed ? (
                            <>
                                {currentTutorial.tipo_arquivo === 'video'
                                    ? 'Aguarde'
                                    : `Aguarde ${countdown}s`}
                            </>
                        ) : (
                            <>
                                {isLastStep ? 'Concluir' : 'Próximo'}
                                <FaChevronRight size={12} style={{ marginLeft: '8px' }} />
                            </>
                        )}
                    </button>
                </S.Actions>
            </S.TutorialCard>
        </S.Container>
    );
}